// The chrome (host shell) entry point.
//
// `mount(rootEl)` is the ONE thing main.ts calls. It:
//   1. Initialises theme from localStorage.
//   2. Registers the chrome's built-in pages (home + placeholders for the
//      pages that other agents will replace).
//   3. Validates the session against `portal.auth.me`.
//   4. Renders either the login screen or the full app shell.
//   5. Wires the router into the content area.

import { api } from "@tkos/api-client";
import { installMiniCrmDelegation, createNotificationsClient } from "@tkos/shared-ui";
import { initTheme } from "./theme";
import { getCurrentUser, getCachedUser, onAuthChange, login } from "./auth";
import { renderForgotPassword } from "./forgot-password";
import {
  needsBillingSetup,
  billingPromptDismissed,
  renderBillingGate,
} from "./billing-gate";
import { startRouter, navigate, listPages, onNavigate } from "./router";
import { promptCreateCrmRecord } from "./create-crm-record";
import { openCustomerComposeModal } from "./customer-compose-modal";
import { initAnalytics, track, trackPageView } from "./analytics";
import { createHeader } from "./header";
import { createMegabar } from "./megabar";
import { customerCache } from "./customer-cache";
import { initRadio } from "./radio";
import { installSpotlightShortcut } from "./spotlight";
import { refreshBranding, startBrandingListener } from "./branding";
import { mountShellFab } from "./fab";
import { mountDemoWatermark } from "./demo-watermark";
import { mountPageAnchors } from "./page-anchors";
import { maybeShowSupplierOnboarding } from "./onboarding-suppliers";
import { maybeShowAiAssistantOnboarding } from "./onboarding-ai-assistant";
import { maybeStartRetailerDemo } from "./demo";
import * as realtime from "./realtime";
import * as roleGate from "./role-gate";
import "./pages-placeholder"; // side-effect: registers stub pages
import type { PageContext } from "./types";

import "./shell.css";

// ── Toast region ─────────────────────────────────────────────────────────────

interface ToastFn {
  (msg: string, kind?: "info" | "success" | "error"): void;
}

function createToaster(): { node: HTMLElement; show: ToastFn } {
  const node = document.createElement("div");
  node.className = "tkos-toaster";
  node.setAttribute("role", "status");
  node.setAttribute("aria-live", "polite");

  const show: ToastFn = (msg, kind = "info") => {
    const t = document.createElement("div");
    t.className = `tkos-toast tkos-toast--${kind}`;
    t.textContent = msg;
    node.appendChild(t);
    window.setTimeout(() => {
      t.classList.add("is-leaving");
      window.setTimeout(() => t.remove(), 250);
    }, 3500);
  };

  return { node, show };
}

// ── Login screen ─────────────────────────────────────────────────────────────

function renderLogin(rootEl: HTMLElement, onSuccess: () => void): void {
  rootEl.replaceChildren();
  const wrap = document.createElement("div");
  wrap.className = "tkos-login";
  wrap.innerHTML = `
    <form class="tkos-login__card" autocomplete="on">
      <div class="tkos-login__brand">
        Kitchengine
      </div>
      <div class="tkos-login__sub">Retailer Portal</div>
      <div class="tkos-login__divider"></div>
      <p class="tkos-login__lede">Sign in to your retailer portal.</p>
      <input
        class="tkos-login__input"
        type="email"
        name="email"
        placeholder="Email"
        autocomplete="username"
        required
      />
      <input
        class="tkos-login__input"
        type="password"
        name="password"
        placeholder="Password"
        autocomplete="current-password"
        required
      />
      <button class="tkos-login__btn" type="submit">Sign in</button>
      <div class="tkos-login__error" data-role="error"></div>
      <button class="tkos-login__link" type="button" data-role="forgot">Forgot password?</button>
    </form>
  `;
  rootEl.appendChild(wrap);

  const form = wrap.querySelector("form")!;
  const emailEl = wrap.querySelector<HTMLInputElement>("input[name='email']")!;
  const input = wrap.querySelector<HTMLInputElement>("input[name='password']")!;
  const btn = wrap.querySelector<HTMLButtonElement>("button[type='submit']")!;
  const errEl = wrap.querySelector<HTMLDivElement>("[data-role='error']")!;

  // Focus the email field shortly after render so it doesn't get clobbered by Vite HMR.
  window.setTimeout(() => emailEl.focus(), 50);

  wrap.querySelector<HTMLButtonElement>("[data-role='forgot']")!.addEventListener("click", () => {
    renderForgotPassword(rootEl, () => renderLogin(rootEl, onSuccess), emailEl.value.trim());
  });

  form.addEventListener("submit", async (e) => {
    e.preventDefault();
    const email = emailEl.value.trim();
    const pw = input.value.trim();
    if (!email || !pw) return;

    btn.disabled = true;
    btn.textContent = "Signing in…";
    errEl.textContent = "";

    try {
      await login(email, pw);
      onSuccess();
    } catch (err) {
      btn.disabled = false;
      btn.textContent = "Sign in";
      input.value = "";
      input.focus();
      errEl.textContent = err instanceof Error ? err.message : "Login failed.";
    }
  });
}

// ── App shell ────────────────────────────────────────────────────────────────

function renderAppShell(
  rootEl: HTMLElement,
  onLoggedOut: () => void,
  opts: { showBillingReminder?: boolean } = {},
): () => void {
  rootEl.replaceChildren();

  const layout = document.createElement("div");
  layout.className = "tkos-layout";

  const closeMobileNav = (): void => {
    layout.classList.remove("is-nav-open");
  };
  // ESC closes the mobile drawer when it's open. Only fires when the
  // drawer is actually open so we don't fight ESC handlers in modals /
  // spotlight / etc.
  const onShellKeydown = (e: KeyboardEvent): void => {
    if (e.key === "Escape" && layout.classList.contains("is-nav-open")) {
      closeMobileNav();
    }
  };
  document.addEventListener("keydown", onShellKeydown);

  const toaster = createToaster();

  // ── Apple-style notifications client ───────────────────────────────────
  // Polls /api/portal/notifications every 30 s (paused while tab is hidden),
  // slides in a toast for any new unread item, and renders a bell + panel
  // in the topbar. Started after auth is established (we're inside the
  // app shell render path which only runs when getCurrentUser() returned
  // a user). Stopped in the shell's teardown.
  const notifications = createNotificationsClient({
    api: (path, init) => api<unknown>(path, init),
  });

  const header = createHeader({
    onToggleNav: () => layout.classList.toggle("is-nav-open"),
    onLoggedOut,
    showToast: toaster.show,
    mountNotifications: (slot) => {
      const handle = notifications.attachBell({
        container: slot,
        onNavigate: (path, item) => {
          // New-lead nudge: a new-submission notification that has no linked CRM
          // record yet offers to create one (pre-filled) instead of dropping the
          // retailer on the CRM/submissions list. If the lead already has a
          // record (customerId set), fall through to normal navigation.
          if (item && item.type === "submission_received" && !item.customerId) {
            void promptCreateCrmRecord({
              name: item.customerName ?? null,
            });
            return;
          }
          // A new-message notification should open the customer's FULL record
          // (where the conversation lives in context), not the generic Messages
          // page. The record page resolves by id, falling back to name.
          if (item && item.type === "message_received" && (item.customerId || item.customerName)) {
            const params: Record<string, string> = {};
            if (item.customerId) params["id"] = item.customerId;
            if (item.customerName) params["name"] = item.customerName;
            navigate("customer", params);
            return;
          }
          // linkPath is URL-style: '/submissions', '/crm?focus=lead-1',
          // '/customer?id=cust-1'. Split off the query string and pull
          // params for the router.
          const [pathPart, query] = path.split("?");
          const name = (pathPart ?? "").replace(/^\//, "") || "dashboard";
          const params: Record<string, string> = {};
          if (query) {
            for (const pair of query.split("&")) {
              const [k, v] = pair.split("=");
              if (k) params[decodeURIComponent(k)] = decodeURIComponent(v ?? "");
            }
          }
          navigate(name, params);
        },
      });
      return () => handle.destroy();
    },
  });

  notifications.start();

  // Real-time bell: a customer message broadcasts `message.received` the moment
  // it lands (unified_messaging). Refresh the notification feed immediately so
  // the bell count + "[Customer] sent you a message" card appear at once
  // instead of waiting up to 30s for the next poll.
  const offMessageNotif = realtime.onEvent("message.received", () => {
    notifications.refresh();
  });

  const megabar = createMegabar({
    showToast: toaster.show,
    onAfterNavigate: closeMobileNav,
  });

  // Append the mobile drawer's utility tray (Kit/Radio/theme/fullscreen/
  // search/settings/apps/sign-out) into the megabar — it lives below the
  // main nav items inside the slide-in drawer on mobile, hidden on desktop.
  // Built and owned by the header so click handlers share state with the
  // (desktop-only) topbar buttons. See header.ts:HeaderHandle.drawerUtils.
  megabar.el.appendChild(header.drawerUtils);

  // Drawer utility buttons that need to close the drawer (navigation,
  // search, apps, settings, sign-out) fire this custom event so we don't
  // have to thread closeMobileNav through every header handler.
  const onCloseMobileNavEvent = (): void => closeMobileNav();
  document.addEventListener("tkos:close-mobile-nav", onCloseMobileNavEvent);

  const main = document.createElement("main");
  main.className = "tkos-main";
  main.id = "tkos-main";
  main.setAttribute("role", "main");
  main.tabIndex = -1;

  // Backdrop for the mobile nav drawer.
  const backdrop = document.createElement("div");
  backdrop.className = "tkos-backdrop";
  backdrop.addEventListener("click", closeMobileNav);

  // Subtle "Powered by Kitchengine" footer attribution — sits below
  // the main content so retailer-branded chrome can dominate the
  // visual hierarchy while the platform credit stays discoverable.
  const poweredBy = document.createElement("div");
  poweredBy.className = "tkos-powered-by";
  poweredBy.setAttribute("role", "contentinfo");
  poweredBy.innerHTML = `
    <span>Powered by</span>
    <span class="tkos-powered-by__mark">Kitchengine</span>
  `;

  layout.append(header.el, megabar.el, main, backdrop, toaster.node, poweredBy);
  rootEl.appendChild(layout);

  // ── Billing reminder (non-blocking) ──────────────────────────────────
  // Shown for an owner who skipped the first-run billing prompt but still
  // hasn't set up their Direct Debit. Sits above <main> so it survives route
  // changes; dismissing hides it for this session only (it returns on the next
  // load until billing is set up). Purely a nudge — access is never blocked;
  // real enforcement of a lapsed subscription is server-side.
  if (opts.showBillingReminder) {
    const reminder = document.createElement("div");
    reminder.className = "tkos-billing-reminder";
    reminder.setAttribute("role", "status");
    reminder.innerHTML = `
      <svg class="tkos-billing-reminder__icon" viewBox="0 0 24 24" width="18" height="18" fill="none"
           stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
        <rect x="3" y="6" width="18" height="13" rx="2.5"/>
        <path d="M3 10.5h18"/>
      </svg>
      <span class="tkos-billing-reminder__text">Finish setting up your billing to keep your account active after the free trial.</span>
      <button type="button" class="tkos-billing-reminder__cta" data-role="billing-setup">Set up now</button>
      <button type="button" class="tkos-billing-reminder__close" data-role="billing-dismiss" aria-label="Dismiss reminder">
        <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" aria-hidden="true">
          <path d="M6 6l12 12M18 6L6 18"/>
        </svg>
      </button>
    `;
    reminder.addEventListener("click", (e) => {
      const t = e.target;
      if (!(t instanceof HTMLElement)) return;
      if (t.closest("[data-role='billing-setup']")) {
        navigate("settings", { tab: "billing" });
      } else if (t.closest("[data-role='billing-dismiss']")) {
        reminder.remove();
      }
    });
    layout.insertBefore(reminder, main);
  }

  // ── Branding (white-label) ───────────────────────────────────────────
  // Fetch the retailer's brand profile, apply --color-amber /
  // --brand-primary / --brand-secondary to :root, and listen for
  // Settings → Branding saves so the chrome re-applies live.
  void refreshBranding();
  const offBrandingEvent = startBrandingListener();

  // ── Realtime (WebSocket push channel) ────────────────────────────────
  // Long-lived ws://<host>/ws connection, cookie-authed. Pages subscribe
  // via realtime.onEvent("contact.updated", …) to get silent in-place
  // updates when their data changes — no polling on the dashboard or CRM.
  // Reconnects automatically; falls back to a 30 s background poll if
  // the WS handshake fails repeatedly. See shell/realtime.ts.
  realtime.start();

  // ── Role gate ────────────────────────────────────────────────────────
  // Fetches the per-retailer permissions matrix and walks every
  // [data-perm] element on first paint, hiding the ones the active role
  // can't access. After each page mount the router fires its nav
  // listeners — we re-apply on the next animation frame so newly-rendered
  // widgets (which the matrix already loaded for) get gated immediately.
  // The "Preview as" selector in Settings → Team & Permissions also
  // calls setCurrentRole() directly to flip the role globally without
  // a reload.
  void roleGate.start(getCachedUser()?.role);
  const offRoleApplyOnNav = onNavigate(() => roleGate.applyRoleSoon());
  // External hook — Team tab dispatches `tkos:role-changed` and
  // `tkos:role-matrix-changed` so the shell can re-apply role-gating
  // without coupling Team → shell directly.
  const onRoleChangedEvent = (e: Event): void => {
    const detail = (e as CustomEvent<{ role?: string }>).detail;
    if (detail && typeof detail.role === "string") {
      roleGate.setCurrentRole(detail.role as roleGate.PortalRole);
    } else {
      roleGate.applyRoleSoon();
    }
  };
  const onMatrixChangedEvent = (e: Event): void => {
    const detail = (e as CustomEvent<{ roles?: Record<string, Record<string, boolean>>; raw?: unknown }>).detail;
    if (detail && detail.roles) {
      roleGate.setMatrix(detail.roles);
    } else if (detail && detail.raw) {
      // Caller passed the raw /role-permissions response — feed it
      // through the canonical setter so perms catalogue updates too.
      roleGate.setMatrixFromResponse(detail.raw as Parameters<typeof roleGate.setMatrixFromResponse>[0]);
    } else {
      roleGate.applyRoleSoon();
    }
  };
  document.addEventListener("tkos:role-changed", onRoleChangedEvent);
  document.addEventListener("tkos:role-matrix-changed", onMatrixChangedEvent);

  // Wire the router.
  const ctx: PageContext = {
    api,
    user: getCachedUser(),
    navigate: (name, params) => navigate(name, params),
    showToast: toaster.show,
    params: {},
    track,
  };

  // Behaviour analytics — wire the unload/visibility flush listeners and
  // capture a page_view on every route change (the central place for it).
  initAnalytics();
  const offNavTrack = onNavigate((name) => trackPageView(name));

  // Pick a default — dashboard is the canonical landing page; fall back
  // to the first registered page only if dashboard somehow failed to load.
  const defaultPage = listPages().find((p) => p.name === "dashboard")?.name
    ?? listPages()[0]?.name
    ?? "dashboard";

  const stop = startRouter({ container: main, ctx, defaultPage });

  // Keep PageContext.user in sync with auth state for any *future* mounts.
  // (Pages already mounted got a snapshot; that's fine — they can subscribe
  // via api directly if they need live-updating user info later.)
  const offAuth = onAuthChange((user) => {
    ctx.user = user;
  });

  // Persistent floating action buttons (Kit / Message / Call), bottom-right.
  // Mounted at shell level so they survive route changes (they live outside
  // <main>). Re-run role-gating so the Message/Call buttons (data-perm=
  // "messages") get hidden for roles that lack the capability.
  const fabCleanup = mountShellFab(ctx);
  // Mobile-only floating "sections on this page" jump menu (bottom-left).
  const anchorsCleanup = mountPageAnchors();
  // Demo-tenant watermark — only renders for the Turner Kitchens demo tenant;
  // a no-op (and self-removing) for every real retailer.
  const demoWatermarkCleanup = mountDemoWatermark(ctx.user);
  roleGate.applyRoleSoon();

  // First-run onboarding: ask a not-yet-onboarded retailer which suppliers they
  // use (off-platform picks queue an admin outreach list). Self-guards via
  // localStorage + ctx.user.onboardingComplete, so this is a no-op for everyone
  // who's already onboarded or answered.
  maybeShowSupplierOnboarding(ctx);
  // After the supplier picker (it defers while that dialog is open), prompt the
  // retailer to name their customer AI assistant. Skippable; one-time.
  maybeShowAiAssistantOnboarding(ctx);
  // Scripted autopilot demo overlay (?demo=retailer) — pure simulation.
  maybeStartRetailerDemo();

  // Install the mini-CRM popup's document-level click delegation. ONE
  // listener fires for every `data-customer-name` element rendered
  // anywhere in the app — see source-of-truth line 17722 for the
  // equivalent behaviour. The lookup reads from the shared customer cache
  // populated by CRM / Contacts / Submissions / ... as they load.
  const stopMiniCrm = installMiniCrmDelegation({
    lookup: (name, focusId) => customerCache.lookup(name, focusId),
    onViewFullRecord: (customer, fallbackName, meta) => {
      // Happy path: cache had a linked CRM contact — straight to the record.
      if (customer?.focusId || customer?.id) {
        const id = customer.focusId ?? customer.id;
        const params: Record<string, string> = { id };
        navigate("customer", params);
        return;
      }
      // Submission row with no CRM contact yet — ask the server to find or
      // create the matching Lead, then navigate to that contact. Mirrors the
      // "every entry point lands in the CRM pipeline" invariant.
      if (meta?.submissionId) {
        void (async (): Promise<void> => {
          try {
            interface LinkResp { ok?: boolean; contact?: { id?: string; name?: string } }
            const resp = await api<LinkResp>(
              `/portal/submissions/${encodeURIComponent(meta.submissionId!)}/link-crm`,
              { method: "POST", body: JSON.stringify({}) },
            );
            const id = resp?.contact?.id;
            if (id) {
              navigate("customer", { id });
            } else {
              toaster.show("Could not open this customer's record.", "error");
            }
          } catch (err) {
            console.warn("[mini-crm] link-crm failed", err);
            toaster.show("Could not open this customer's record.", "error");
          }
        })();
        return;
      }
      // Fallback: name-only navigation (existing behaviour for non-submission
      // call sites that don't carry a CRM id, e.g. dashboard message rows).
      const name = customer?.name ?? fallbackName;
      const params: Record<string, string> = {};
      if (name) params["name"] = name;
      navigate("customer", params);
    },
    onCall: (customer) => {
      // DEFRAUD-SACRED: never `tel:` ([[feedback-contact-masking]]).
      // Hit the Twilio-masked dial endpoint with the customer id; the
      // number stays server-side. While Twilio integration is gated on
      // Phase 3 sign-off, the endpoint responds 503 — we toast a
      // friendly "coming soon" message rather than fall back to tel:.
      void (async () => {
        try {
          await api<{ ok: boolean }>("/portal/calls/dial", {
            method: "POST",
            body: JSON.stringify({ customerId: customer.id }),
          });
          toaster.show("Connecting call…", "success");
        } catch (err) {
          // ApiError carries status; 503 means Twilio not wired (Phase 3).
          const status = (err as { status?: number } | null)?.status;
          if (status === 503) {
            toaster.show("Click-to-call goes live with Twilio (Phase 3).", "info");
          } else {
            toaster.show("Could not start call", "error");
          }
        }
      })();
    },
    onMessage: (customer) => {
      // Open this customer's conversation inline — single click, no searching.
      // The compose modal loads the existing thread if one exists, else starts
      // a new one (find-or-create is handled server-side, keyed on the
      // customer's email via customerId). Mirrors the CRM page's Message action
      // (crm/page.ts). The masked email is fine: /portal/customer-message
      // resolves the real address server-side from customerId.
      if (!customer?.id) {
        navigate("messages");
        return;
      }
      const ownRetailerId =
        (typeof localStorage !== "undefined" ? localStorage.getItem("kos_retailer_id") : null) ??
        ctx.user?.retailerId ?? "";
      openCustomerComposeModal(
        {
          customer: { id: customer.id, name: customer.name, email: customer.email ?? "" },
          ownRetailerId,
          retailerName: ctx.user?.retailerName ?? "Retailer",
        },
        ctx,
      );
    },
  });

  // Central handling for server-pushed contact deletions. One shell-level
  // subscription so every tab stays consistent regardless of which page is
  // open: evict the shared mini-CRM cache (no stale popups), then re-dispatch
  // a `tkos:customer-deleted` window event so the CRM / Contacts list pages
  // refresh via the same decoupled pattern they use for `tkos:customer-created`.
  const offContactDeleted = realtime.onEvent<{ contactId?: string }>(
    "contact.deleted",
    (ev) => {
      const contactId = ev.data?.contactId;
      if (!contactId) return;
      customerCache.remove(contactId);
      window.dispatchEvent(
        new CustomEvent("tkos:customer-deleted", { detail: { contactId } }),
      );
    },
  );

  // Cmd/Ctrl-K → Spotlight overlay (global keyboard shortcut)
  const stopSpotlight = installSpotlightShortcut({ showToast: toaster.show });

  return () => {
    fabCleanup();
    anchorsCleanup();
    demoWatermarkCleanup();
    offContactDeleted();
    stopSpotlight();
    stopMiniCrm();
    stop();
    offAuth();
    offBrandingEvent();
    notifications.stop();
    document.removeEventListener("keydown", onShellKeydown);
    document.removeEventListener("tkos:close-mobile-nav", onCloseMobileNavEvent);
    document.removeEventListener("tkos:role-changed", onRoleChangedEvent);
    document.removeEventListener("tkos:role-matrix-changed", onMatrixChangedEvent);
    offRoleApplyOnNav();
    offNavTrack();
    offMessageNotif();
    header.destroy();
    megabar.destroy();
  };
}

// ── Public entry ─────────────────────────────────────────────────────────────

let teardownShell: (() => void) | null = null;

/**
 * Mount the chrome into `rootEl`. Idempotent — calling again tears down the
 * previous mount (handy for HMR-safety in development).
 */
export async function mount(rootEl: HTMLElement): Promise<void> {
  if (teardownShell) {
    teardownShell();
    teardownShell = null;
  }

  initTheme();
  initRadio();

  // Initial loading flash so the user doesn't see a blank white screen
  // while we hit `auth.me`.
  rootEl.replaceChildren();
  const loading = document.createElement("div");
  loading.className = "tkos-bootstrap";
  loading.textContent = "Loading…";
  rootEl.appendChild(loading);

  const renderForCurrentAuth = async (): Promise<void> => {
    const user = await getCurrentUser();
    if (!user) {
      if (teardownShell) {
        teardownShell();
        teardownShell = null;
      }
      renderLogin(rootEl, () => {
        // After successful login: re-render the shell with auth state populated.
        void renderForCurrentAuth();
      });
      return;
    }

    // First-run billing prompt (subscription-first onboarding). Only the
    // account OWNER is prompted, once, to set up their Direct Debit mandate;
    // everyone else — and owners who've already set it up or skipped — goes
    // straight in. This is a NON-BLOCKING nudge: "Skip for now" grants full
    // access and leaves a persistent reminder. Real enforcement of a lapsed
    // subscription lives server-side (trial/dunning/suspension), NOT here.
    // Replaces the old mandatory Stripe wall — platform access is no longer
    // gated on Stripe.
    const isOwner = (user.role ?? "owner") === "owner";
    const needsSetup = isOwner ? await needsBillingSetup() : false;

    if (needsSetup && !billingPromptDismissed()) {
      if (teardownShell) {
        teardownShell();
        teardownShell = null;
      }
      renderBillingGate(rootEl, {
        onRecheck: () => void renderForCurrentAuth(),
        onSkip: () => void renderForCurrentAuth(),
        onLoggedOut: () => void renderForCurrentAuth(),
      });
      return;
    }

    // Owner who skipped but still hasn't set up billing → persistent reminder.
    const showBillingReminder = needsSetup;

    teardownShell = renderAppShell(
      rootEl,
      () => {
        // After logout: re-check auth (which will now return null) and render login.
        void renderForCurrentAuth();
      },
      { showBillingReminder },
    );
  };

  await renderForCurrentAuth();
}
